import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Session, SessionCreate, SessionCreateResponse, SessionStats, SessionFilter, MediaFile, Composition, MediaResponse } from '../models/session.model';

@Injectable({
  providedIn: 'root'
})
export class SessionManagerService {
  private apiUrl = '/api'; // Will be proxied to the NestJS API
  private sessionsSubject = new BehaviorSubject<Session[]>([]);
  public sessions$ = this.sessionsSubject.asObservable();

  constructor(private http: HttpClient) {}

  // Session CRUD Operations
  getAllSessions(filter?: SessionFilter): Observable<Session[]> {
    // For now, ignore filters since backend doesn't support them yet
    return this.http.get<Session[]>(`${this.apiUrl}/session`);
  }

  getSession(sessionId: string): Observable<Session> {
    return this.http.get<Session>(`${this.apiUrl}/session/${sessionId}`);
  }

  createSession(sessionData: SessionCreate): Observable<SessionCreateResponse> {
    return this.http.get<SessionCreateResponse>(`${this.apiUrl}/session/create/${sessionData.sessionId}`);
  }

  updateSession(sessionId: string, sessionData: Partial<Session>): Observable<Session> {
    // Update not implemented in backend yet
    throw new Error('Session update not implemented');
  }

  deleteSession(sessionId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/session/${sessionId}`);
  }

  // Bulk Operations
  deleteSessions(sessionIds: string[]): Observable<void> {
    // Implement by calling individual delete endpoints
    const deleteObservables = sessionIds.map(id => this.deleteSession(id));
    return new Observable(observer => {
      Promise.all(deleteObservables.map(obs => obs.toPromise()))
        .then(() => {
          observer.next();
          observer.complete();
        })
        .catch(error => observer.error(error));
    });
  }

  // Media File Management
  getSessionMediaFiles(sessionId: string): Observable<MediaFile[]> {
    return this.http.get<MediaResponse>(`${this.apiUrl}/media/session/${sessionId}`)
      .pipe(map(response => response.mediaFiles));
  }

  deleteMediaFile(mediaFileId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/media/${mediaFileId}`);
  }

  downloadMediaFile(mediaFileId: string): Observable<Blob> {
    // Use the download service to get the media file URL first, then download it
    return this.http.get(`${this.apiUrl}/media/${mediaFileId}`, { 
      responseType: 'blob' 
    });
  }

  // Composition Management
  getSessionCompositions(sessionId: string): Observable<Composition[]> {
    return this.http.get<any>(`${this.apiUrl}/media/session/${sessionId}/compositions`)
      .pipe(
        map(response => {
          // The API returns a structure with { sessionId, compositions, summary }
          return response.compositions || [];
        })
      );
  }

  createComposition(sessionId: string): Observable<Composition> {
    return this.http.post<Composition>(`${this.apiUrl}/media/createVideoComposition`, {
      sessionId: sessionId
    });
  }

  downloadComposition(compositionId: string): Observable<Blob> {
    // This would need to be implemented based on the actual composition storage
    return this.http.get(`${this.apiUrl}/media/composition/${compositionId}`, { 
      responseType: 'blob' 
    });
  }

  reprocessComposition(sessionId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/media/session/${sessionId}/reprocess-composition`, {});
  }

  regenerateSessionUrls(sessionId: string): Observable<SessionCreateResponse> {
    return this.http.post<SessionCreateResponse>(`${this.apiUrl}/session/${sessionId}/regenerate-urls`, {});
  }

  // Statistics and Analytics
  getSessionStats(): Observable<SessionStats> {
    return this.http.get<SessionStats>(`${this.apiUrl}/session/stats`);
  }

  getSessionAnalytics(sessionId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/sessions/${sessionId}/analytics`);
  }

  // Utility methods
  refreshSessions(filter?: SessionFilter): void {
    this.getAllSessions(filter).subscribe(sessions => {
      this.sessionsSubject.next(sessions);
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatDuration(seconds: number): string {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}h ${mins}m ${secs}s`;
    } else if (mins > 0) {
      return `${mins}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }

  getRoleLabel(role: number): string {
    return role === 1 ? 'Host' : 'Guest';
  }

  getStatusLabel(composition: Composition): string {
    switch (composition.jobStatus?.toLowerCase()) {
      case 'completed': return 'Completed';
      case 'processing': return 'Processing';
      case 'failed': return 'Failed';
      case 'pending': return 'Pending';
      default: return 'Unknown';
    }
  }

  // Generate a unique session ID (8 characters, alphanumeric)
  generateSessionId(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }
}
